import { useState, useCallback } from "react";
import { App } from "antd";
import { useMediaQuery } from "usehooks-ts";
import ResponsiveModal from "../components/ResponsiveModal";

interface ConfirmOptions {
  title: React.ReactNode;
  content: React.ReactNode;
  onOk?: () => void | Promise<void>;
  okText?: string;
  cancelText?: string;
  danger?: boolean;
  icon?: React.ReactNode;
}

/**
 * Hook to use ResponsiveModal programmatically
 * - Desktop: Uses native Ant Design modal.confirm
 * - Mobile: Uses custom bottom sheet drawer
 * 
 * @example
 * const { confirm, contextHolder } = useResponsiveModal();
 * 
 * const handleDelete = () => {
 *   confirm({
 *     title: "حذف آیتم",
 *     content: "آیا از حذف اطمینان دارید؟",
 *     danger: true,
 *     onOk: async () => {
 *       await deleteItem();
 *     },
 *   });
 * };
 */
export function useResponsiveModal() {
  const { modal } = App.useApp();
  const isMobile = useMediaQuery("(max-width: 768px)");

  const [modalState, setModalState] = useState<{
    open: boolean;
    options: ConfirmOptions | null;
    loading: boolean;
  }>({
    open: false,
    options: null,
    loading: false,
  });

  const confirm = useCallback((options: ConfirmOptions) => {
    // Desktop: Use native Ant Design modal.confirm
    if (!isMobile) {
      modal.confirm({
        title: options.title,
        content: options.content,
        icon: options.icon,
        okText: options.okText,
        cancelText: options.cancelText,
        okButtonProps: { danger: options.danger },
        onOk: options.onOk,
      });
    } else {
      // Mobile: Use custom ResponsiveModal drawer
      setModalState({
        open: true,
        options,
        loading: false,
      });
    }
  }, [isMobile, modal]);

  const handleOk = useCallback(async () => {
    if (!modalState.options?.onOk) {
      setModalState((prev) => ({ ...prev, open: false }));
      return;
    }

    try {
      setModalState((prev) => ({ ...prev, loading: true }));
      await modalState.options.onOk();
      setModalState({ open: false, options: null, loading: false });
    } catch (error) {
      setModalState((prev) => ({ ...prev, loading: false }));
      // Don't close modal on error
    }
  }, [modalState.options]);

  const handleClose = useCallback(() => {
    setModalState({ open: false, options: null, loading: false });
  }, []);

  // Mobile only: Render custom drawer
  const contextHolder = isMobile && modalState.options ? (
    <ResponsiveModal
      open={modalState.open}
      onClose={handleClose}
      onOk={handleOk}
      title={modalState.options.title}
      type="confirm"
      confirmLoading={modalState.loading}
      okText={modalState.options.okText}
      cancelText={modalState.options.cancelText}
      danger={modalState.options.danger}
      icon={modalState.options.icon}
    >
      {modalState.options.content}
    </ResponsiveModal>
  ) : null;

  return {
    confirm,
    contextHolder,
  };
}

export default useResponsiveModal;

